import React, { useState, useEffect, useRef, useMemo, memo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useChatAI } from "../chat-ai-context";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { DatePicker } from "@/components/application/date-picker/date-picker";
import { Avatar } from "@/components/base/avatar/avatar";
import { containerService } from "@/data/mock-containers";
import { collaborationService } from "@/data/mock-collaborations";
import { accountService } from "@/data/mock-accounts";
import { today, getLocalTimeZone, CalendarDate } from "@internationalized/date";
import { storage } from "@/lib/local-storage";

interface ContainerWizardProps {
    onCancel: () => void;
}

type Step =
    | "containerNumber"
    | "routeFrom"
    | "routeTo"
    | "eta"
    | "valueAmount"
    | "valueCurrency"
    | "status"
    | "receiverId"
    | "shippingCompany"
    | "review"
    | "completed";

// Componente memoizzato per i messaggi dell'assistente
const AssistantMessage = memo(({ question, avatar }: { question: string; avatar: React.ReactElement }) => {
    return (
        <div className="flex items-start">
            {avatar}
            <div className="flex-1">
                <div className="max-w-[520px] rounded-2xl border border-secondary bg-primary px-4 py-3 text-sm leading-6 text-primary shadow-xs">
                    {question}
                </div>
            </div>
        </div>
    );
});

AssistantMessage.displayName = "AssistantMessage";

export const ContainerWizard = ({ onCancel }: ContainerWizardProps) => {
    const { t } = useTranslation(['common', 'pages:containers', 'types']);
    const navigate = useNavigate();
    const { wizardFormData, setWizardFormData, appendMessage, closeDrawer } = useChatAI();
    const listRef = useRef<HTMLDivElement | null>(null);

    const [currentStep, setCurrentStep] = useState<Step>("containerNumber");
    const [receiverOptions, setReceiverOptions] = useState<Array<{ id: string; label: string }>>([]);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const steps: Step[] = [
        "containerNumber",
        "routeFrom",
        "routeTo",
        "eta",
        "valueAmount",
        "valueCurrency",
        "status",
        "receiverId",
        "shippingCompany",
        "review",
    ];

    useEffect(() => {
        // Load receivers
        const accountId = storage.getItem('accountId') || '1';
        const collaborations = collaborationService.getByShipperId(accountId);
        const activeCollaborations = collaborations.filter(c => c.status === 'Active');
        const allAccounts = accountService.getAll();
        const receiverAccounts = allAccounts.filter(acc => acc.userType === 'RECEIVER');

        const options = activeCollaborations.map(collab => {
            const receiverAccount = receiverAccounts.find(acc => acc.id === collab.receiverId);
            return receiverAccount ? {
                id: receiverAccount.id,
                label: `${receiverAccount.legalName} (${receiverAccount.country})`,
            } : null;
        }).filter(Boolean) as Array<{ id: string; label: string }>;

        setReceiverOptions(options);
    }, []);

    useEffect(() => {
        // Initialize input value from form data
        if (wizardFormData[currentStep]) {
            setInputValue(String(wizardFormData[currentStep]));
        } else {
            setInputValue("");
        }
    }, [currentStep, wizardFormData]);

    useEffect(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    }, [currentStep]);

    const getStepQuestion = (step: Step): string => {
        switch (step) {
            case "containerNumber":
                return t('aiChat.wizard.container.containerNumber');
            case "routeFrom":
                return t('aiChat.wizard.container.routeFrom');
            case "routeTo":
                return t('aiChat.wizard.container.routeTo');
            case "eta":
                return t('aiChat.wizard.container.eta');
            case "valueAmount":
                return t('aiChat.wizard.container.valueAmount');
            case "valueCurrency":
                return t('aiChat.wizard.container.valueCurrency');
            case "status":
                return t('aiChat.wizard.container.status');
            case "receiverId":
                return t('aiChat.wizard.container.receiverId');
            case "shippingCompany":
                return t('aiChat.wizard.container.shippingCompany');
            default:
                return "";
        }
    };

    const handleAnswer = (value: string) => {
        const updatedData = { ...wizardFormData, [currentStep]: value };
        setWizardFormData(updatedData);

        // Add user message
        appendMessage({ role: "user", content: value });

        // Add assistant confirmation
        setTimeout(() => {
            appendMessage({ role: "assistant", content: t('aiChat.wizard.confirmed') });

            // Move to next step
            const currentIndex = steps.indexOf(currentStep);
            if (currentIndex < steps.length - 1) {
                setTimeout(() => {
                    setCurrentStep(steps[currentIndex + 1]);
                    appendMessage({ role: "assistant", content: getStepQuestion(steps[currentIndex + 1]) });
                }, 500);
            } else {
                // Review step
                setTimeout(() => {
                    setCurrentStep("review");
                    showReview();
                }, 500);
            }
        }, 500);
    };

    const showReview = () => {
        const reviewMessage = t('aiChat.wizard.container.review', {
            containerNumber: wizardFormData.containerNumber,
            routeFrom: wizardFormData.routeFrom,
            routeTo: wizardFormData.routeTo,
            eta: wizardFormData.eta,
            valueAmount: wizardFormData.valueAmount,
            valueCurrency: wizardFormData.valueCurrency,
            status: t(`containerStatus.${wizardFormData.status}`, { ns: 'types' }),
            receiver: receiverOptions.find(r => r.id === wizardFormData.receiverId)?.label,
            shippingCompany: wizardFormData.shippingCompany || t('aiChat.wizard.notSpecified'),
        });
        appendMessage({ role: "assistant", content: reviewMessage });
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            const accountId = storage.getItem('accountId') || '1';
            const valueAmount = parseFloat(wizardFormData.valueAmount);

            // Validation
            if (!wizardFormData.containerNumber || !wizardFormData.routeFrom || !wizardFormData.routeTo ||
                !wizardFormData.eta || !wizardFormData.valueAmount || !wizardFormData.receiverId) {
                appendMessage({ role: "assistant", content: t('form.errors.allFieldsRequired', { ns: 'common' }) });
                setIsLoading(false);
                return;
            }

            // Check if container number already exists
            const existingContainer = containerService.getAll().find(c =>
                c.containerNumber?.toLowerCase() === wizardFormData.containerNumber.toLowerCase()
            );
            if (existingContainer) {
                appendMessage({ role: "assistant", content: t('form.errors.containerExists', { ns: 'pages:containers' }) });
                setIsLoading(false);
                return;
            }

            // Create container
            let etaDate: Date;
            if (wizardFormData.eta instanceof CalendarDate) {
                etaDate = wizardFormData.eta.toDate(getLocalTimeZone());
            } else if (typeof wizardFormData.eta === 'string') {
                etaDate = new Date(wizardFormData.eta);
            } else {
                etaDate = new Date();
            }

            const newContainer = containerService.create({
                shipperId: accountId,
                receiverId: wizardFormData.receiverId,
                containerNumber: wizardFormData.containerNumber,
                route: {
                    from: wizardFormData.routeFrom,
                    to: wizardFormData.routeTo
                },
                progress: 0,
                eta: etaDate,
                documentStatus: 'Pending',
                riskLevel: 'Low',
                value: {
                    amount: valueAmount,
                    currency: wizardFormData.valueCurrency || 'USD'
                },
                status: wizardFormData.status as any || 'In Transit',
                shippingCompany: wizardFormData.shippingCompany || undefined,
                commodity: 'electronics'
            });

            appendMessage({ role: "assistant", content: t('aiChat.wizard.container.success') });
            setCurrentStep("completed");

            setTimeout(() => {
                closeDrawer();
                navigate(`/shipper/containers/${newContainer.id}`);
            }, 2000);
        } catch (error) {
            appendMessage({ role: "assistant", content: t('form.errors.generic', { ns: 'common' }) });
        } finally {
            setIsLoading(false);
        }
    };

    const renderInput = () => {
        if (currentStep === "review") {
            return (
                <div className="space-y-3">
                    <Button onClick={handleSubmit} color="primary" size="lg" isDisabled={isLoading}>
                        {isLoading ? t('form.saving', { ns: 'common' }) : t('aiChat.wizard.create')}
                    </Button>
                    <Button onClick={onCancel} color="secondary" size="lg">
                        {t('buttons.cancel')}
                    </Button>
                </div>
            );
        }

        if (currentStep === "completed") {
            return (
                <div className="text-center text-primary">
                    {t('aiChat.wizard.container.completed')}
                </div>
            );
        }

        if (currentStep === "eta") {
            const currentDate = wizardFormData.eta
                ? (wizardFormData.eta instanceof CalendarDate
                    ? wizardFormData.eta
                    : new CalendarDate(new Date(wizardFormData.eta).getFullYear(), new Date(wizardFormData.eta).getMonth() + 1, new Date(wizardFormData.eta).getDate()))
                : null;

            return (
                <DatePicker
                    value={currentDate}
                    onChange={(date) => {
                        if (date) {
                            handleAnswer(date.toDate(getLocalTimeZone()).toISOString());
                        }
                    }}
                    minValue={today(getLocalTimeZone())}
                />
            );
        }

        if (currentStep === "valueCurrency") {
            const currencyOptions = [
                { id: 'USD', label: 'USD' },
                { id: 'EUR', label: 'EUR' },
                { id: 'GBP', label: 'GBP' },
                { id: 'CHF', label: 'CHF' }
            ];
            return (
                <div className="space-y-2">
                    {currencyOptions.map((option) => (
                        <Button
                            key={option.id}
                            onClick={() => handleAnswer(option.id)}
                            color="secondary"
                            size="lg"
                            className="w-full"
                        >
                            {option.label}
                        </Button>
                    ))}
                </div>
            );
        }

        if (currentStep === "status") {
            const statusOptions = [
                { id: 'In Transit', label: t('containerStatus.inTransit', { ns: 'types' }) },
                { id: 'At Port', label: t('containerStatus.atPort', { ns: 'types' }) },
                { id: 'Delayed', label: t('containerStatus.delayed', { ns: 'types' }) },
                { id: 'Delivered', label: t('containerStatus.delivered', { ns: 'types' }) }
            ];
            return (
                <div className="space-y-2">
                    {statusOptions.map((option) => (
                        <Button
                            key={option.id}
                            onClick={() => handleAnswer(option.id)}
                            color="secondary"
                            size="lg"
                            className="w-full"
                        >
                            {option.label}
                        </Button>
                    ))}
                </div>
            );
        }

        if (currentStep === "receiverId") {
            return (
                <div className="space-y-2">
                    {receiverOptions.map((option) => (
                        <Button
                            key={option.id}
                            onClick={() => handleAnswer(option.id)}
                            color="secondary"
                            size="lg"
                            className="w-full"
                        >
                            {option.label}
                        </Button>
                    ))}
                </div>
            );
        }

        // Open-ended questions
        return (
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    if (inputValue.trim()) {
                        handleAnswer(inputValue.trim());
                        setInputValue("");
                    }
                }}
                className="space-y-3"
            >
                <Input
                    value={inputValue}
                    onChange={setInputValue}
                    placeholder={t('aiChat.wizard.enterValue')}
                    size="md"
                />
                <div className="flex gap-2">
                    <Button type="submit" color="primary" size="md">
                        {t('buttons.next')}
                    </Button>
                    <Button type="button" onClick={onCancel} color="secondary" size="md">
                        {t('buttons.cancel')}
                    </Button>
                </div>
            </form>
        );
    };

    // Memoizzare il componente avatar per evitare re-render inutili
    const AssistantAvatar = useMemo(() => {
        return <Avatar size="sm" src="/icons/ICO-ask-eve-hover.svg" status="online" className="mr-3" />;
    }, []);

    // Memoizzare la domanda dello step corrente per evitare re-render quando cambia solo inputValue
    const currentStepQuestion = useMemo(() => {
        return getStepQuestion(currentStep);
    }, [currentStep, t]);

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div ref={listRef} className="flex-1 overflow-y-auto py-4 md:py-6">
                <div className="flex flex-col gap-5">
                    <AssistantMessage
                        question={currentStepQuestion}
                        avatar={AssistantAvatar}
                    />

                    {renderInput()}
                </div>
            </div>
        </div>
    );
};

